import React, { useMemo } from 'react';
import { Modal, Button, SelectHierarchyLabel, ToastContext, RadioLabel } from '@weezevent/nacre';
import { useForm, fetcher } from '@weezevent/weezjs-core';
import { BusinessEntity, Context, LegalEntity, Organization, Role } from 'models';
import { LoadingOptions } from 'components';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'models/user/permissions';
import { filterRoles, matchSearch } from '../../../../utils/utils';

const DEFAULT_PARAMS = { search: '', limit: 10, offset: 0 };
const DEFAULT_FORM = {
    role: null,
    level: null,
    context: []
};

const RoleCreationModal = ({ open, organizationId = null, user, users = null, roles, removeMode = false, onClose, sync, onBulkAction = null }) => {
    const { t } = useTranslation();
    const permissions = usePermissions();
    const isAdmin = permissions.isUserAdmin();
    const isDev = permissions.isUserDev();
    const toast = React.useContext(ToastContext);
    const [contextMode, setContextMode] = React.useState('all_organisations');
    const [queryParams, setQueryParams] = React.useState(DEFAULT_PARAMS);
    const [searchRole, setSearchRole] = React.useState('');
    const [searchContextValue, setSearchContextValue] = React.useState('');

    const [form, setForm, setFormObject] = useForm({ ...DEFAULT_FORM });

    const isBatch = Boolean(users && users.length > 0);
    const selectedUsers = isBatch ? users : [user].filter(Boolean);

    const [organizations, loadingOrganizations] = Organization.useApiModel(
        {
            cache: false,
            allow_cache: false,
            query: queryParams,
            launch: open,
            onSuccess: () => {
                if (organizationId) {
                    setForm('organization', Number(organizationId));
                }
            }
        },
        [queryParams, open, organizationId]
    );

    const [legalEntities, loadingLegalEntity] = LegalEntity.useApiModel({
        launch: open && !organizationId && !removeMode
    });

    const [businessEntities, loadingBusinessEntity] = BusinessEntity.useApiModel({
        launch: open && !organizationId && !removeMode
    });

    const [organization, loadingOrganization] = Organization.useApiModel(
        {
            id: organizationId,
            launch: open && Boolean(organizationId)
        },
        [open, organizationId]
    );

    // Get available users' roles for remove mode
    const availableRoles = React.useMemo(() => {
        if (!removeMode || !selectedUsers || !roles) return roles;

        // For batch remove, find common roles across all selected users
        if (isBatch) {
            const allUserRoles = selectedUsers.map(u => u.user_roles || []);
            if (allUserRoles.length === 0) return [];

            const commonRoleSlugs = allUserRoles[0].map(it => it.role.slug).filter(slug => allUserRoles.every(userRoles => userRoles.some(it => it.role.slug === slug)));
            return roles.filter(role => commonRoleSlugs.includes(role.slug));
        }

        // For single user remove, show only its roles
        const userRoleSlugs = (user?.user_roles || []).map(it => it.role.slug);
        return roles.filter(role => userRoleSlugs.includes(role.slug));
    }, [removeMode, selectedUsers, roles, isBatch, user]);

    const organizationsOptions = React.useMemo(() => {
        if (loadingOrganizations) {
            return LoadingOptions;
        }
        if (!organizations) {
            return [];
        }
        if (organizationId) {
            return [organization.toOption()];
        }
        if (queryParams.search) {
            return organizations.filter(contextValues => matchSearch([contextValues.object.name, contextValues.object.slug], searchContextValue)).map(org => org.toOption());
        }
        return [...organizations?.map(org => org.toOption())];
    }, [organization, organizations, loadingOrganization, loadingOrganizations, LoadingOptions, organizationId, searchContextValue]);

    const rolesOptions = role => {
        const rolesList = removeMode ? availableRoles : roles;
        if (!rolesList) {
            return LoadingOptions;
        }
        if (isAdmin) {
            return rolesList.filter(role => matchSearch([role.object.name, role.object.slug], searchRole)).map(role => role.toOption());
        }
        if (role) {
            // Return roles while applying the search filter
            return filterRoles(role, rolesList, permissions)
                .filter(role => matchSearch([role.object.name, role.object.slug], searchRole))
                .map(role => role.toOption());
        }
        return [];
    };

    const contextOptions = useMemo(() => {
        return [
            {
                key: '0',
                text: t('common.legal-entity'),
                name: t('common.legal-entity'),
                value: 0,
                disabled: !permissions._roles.some(r => r.context.level === 1) && !isDev
            },
            {
                key: '1',
                text: t('common.business-entity'),
                name: t('common.business-entity'),
                value: 1,
                disabled: !permissions._roles.some(r => r.context.level === 2) && !isDev
            },
            { key: '2', text: t('common.organization'), name: t('common.organization'), value: 2 }
        ];
    }, [permissions]);

    // No need for callbacks here
    const handleSearch = value => {
        if (!value) {
            setQueryParams(DEFAULT_PARAMS);
        }
        setQueryParams({ ...DEFAULT_PARAMS, search: value });
    };

    const handleContext = React.useCallback(
        value => {
            setFormObject({ ...form, level: value, context: [], role: null });
        },
        [form]
    );

    const handleContextMode = React.useCallback(
        value => {
            handleContext(null);
            setContextMode(value);
        },
        [form]
    );

    const handleRole = (name, value) => {
        if (organizationId) {
            form[name] = ['*', '*', organizationId];
        }

        if (name === 'context') {
            form[name] = ['*', '*', '*'];
            if (form[name]) {
                form[name][form['level']] = value;
            }
            form['role'] = null;
        } else {
            form[name] = value;
        }
        setForm(name, form[name]);
    };

    const handleBatchAddRole = (_role, closeAfter) => {
        onBulkAction({
            action: 'bulk_add_role',
            config: { role_slug: _role.slug, context: form.context || [] }
        });
        if (closeAfter) {
            onClose();
        } else {
            setFormObject({ role: null }, true);
        }
        return Promise.resolve();
    };

    const handleBatchRemoveRole = (_role, closeAfter) => {
        onBulkAction({
            action: 'bulk_remove_role',
            config: { role_slug: _role.slug, context: form.context || [] }
        });
        if (closeAfter) onClose();
        return Promise.resolve();
    };

    const handleSingleAddRole = (_role, closeAfter) => {
        const { username } = user;
        let payload = {
            context: form.context,
            user: {
                username
            }
        };

        if (form.context.length > 0) {
            payload.context['value'] = form.context;
        }

        const role = new Role({ ...payload, pk: _role.slug });

        return role
            .save({
                slug: _role.slug,
                organizationId
            })
            .then(response => {
                if (response.status === 201 || response.status === 200) {
                    toast.success(t('sauron.toasts.roles.add-role-success'));
                }
                sync();
                if (closeAfter) {
                    setFormObject({ role: null, level: null, context: [] }, true);
                } else {
                    setFormObject({ role: null }, true);
                }
            })
            .catch(() => {
                toast.error(t('sauron.toasts.error'));
            })
            .finally(() => {
                if (closeAfter) {
                    onClose();
                }
            });
    };

    const handleSingleRemoveRole = (_role, closeAfter) => {
        const { username } = user;
        const userRole = user.user_roles?.find(ur => ur.role.slug === _role.slug);
        const payload = {
            context: new Context(userRole?.context).values,
            user: {
                username
            }
        };

        const url = `/ticket/roles/${_role.slug}/users/`;

        return fetcher
            .delete(url, { data: payload })
            .then(response => {
                if (response.status === 204) {
                    toast.success(t('sauron.user.remove-role-success'));
                }
                sync();
                setFormObject({ role: null, level: null, context: [] }, true);
            })
            .catch(() => {
                toast.error(t('sauron.toasts.error'));
            })
            .finally(() => {
                if (closeAfter) {
                    onClose();
                }
            });
    };

    const handleSubmit = (closeAfter = false) => {
        const _role = roles.find(role => role.slug === form.role);

        if (!_role) {
            toast.error(t('sauron.toasts.error'));
            return Promise.reject();
        }

        if (isBatch) {
            return removeMode ? handleBatchRemoveRole(_role, closeAfter) : handleBatchAddRole(_role, closeAfter);
        }

        return removeMode ? handleSingleRemoveRole(_role, closeAfter) : handleSingleAddRole(_role, closeAfter);
    };

    const currentContextValue = React.useMemo(() => {
        if (!form.context || !form.context.length) {
            return null;
        }
        return form.context[form.level];
    }, [form.context]);

    const valuesOptions = React.useMemo(() => {
        switch (form.level) {
            case 0:
                return loadingLegalEntity || !legalEntities
                    ? []
                    : legalEntities.filter(le => matchSearch([le.object.name, le.object.slug], searchContextValue)).map(le => le.toOption());
            case 1:
                return loadingBusinessEntity || !businessEntities
                    ? []
                    : businessEntities.filter(be => matchSearch([be.object.name, be.object.slug], searchContextValue)).map(le => le.toOption());
            case 2:
                return !organizations ? [] : organizationsOptions;
            default:
                return [];
        }
    }, [form.level, searchContextValue, loadingOrganizations]);

    const modalTitle = isBatch
        ? removeMode
            ? t('sauron.user.remove-role-batch')
            : t('sauron.user.add-role-batch')
        : removeMode
        ? t('sauron.user.remove-role')
        : t('sauron.user.add-role');

    const modalSubtitle = isBatch
        ? t('sauron.user.batch-role-subtitle', { count: selectedUsers.length })
        : removeMode
        ? t('sauron.user.remove-role-subtitle')
        : t('sauron.user.add-role-subtitle');

    return (
        <Modal
            size={'large'}
            open={open}
            allowExternalClosing={false}
            onClose={onClose}
            title={modalTitle}
            subtitle={modalSubtitle}
            customFooter={
                <Button.Group>
                    <Button onClick={onClose} label={t('common.cta.cancel')} />
                    {removeMode ? (
                        <>
                            <Button primary inverted onClick={() => handleSubmit(false)} disabled={!form.role} label={t('sauron.user.remove-role-continue')} />
                            <Button primary onClick={() => handleSubmit(true)} disabled={!form.role} label={t('sauron.user.remove-role-close')} />
                        </>
                    ) : (
                        <>
                            <Button
                                primary
                                inverted
                                onClick={() => handleSubmit(false)}
                                disabled={!form.role}
                                label={removeMode ? t('sauron.user.remove-role-action') : t('sauron.user.add-role-action')}
                            />
                            <Button primary onClick={() => handleSubmit(true)} disabled={!form.role} label={t('common.cta.save')} />
                        </>
                    )}
                </Button.Group>
            }
        >
            <>
                {!removeMode && (
                    <>
                        <div>{t('sauron.roles.select-role-option.label')}</div>
                        <RadioLabel
                            bordered
                            inline
                            name="context_mode"
                            value="all_organisations"
                            checked={contextMode === 'all_organisations'}
                            onChange={() => handleContextMode('all_organisations')}
                        >
                            {t('sauron.roles.select-role-option.all-organisations')}
                        </RadioLabel>
                        <RadioLabel
                            bordered
                            inline
                            name="context_mode"
                            value="specific_context"
                            checked={contextMode === 'specific_context'}
                            onChange={() => handleContextMode('specific_context')}
                        >
                            {t('sauron.roles.select-role-option.specific-context')}
                        </RadioLabel>
                        {contextMode === 'specific_context' && (
                            <>
                                <SelectHierarchyLabel
                                    searchable={false}
                                    disabled={organizationId}
                                    value={form.level}
                                    options={contextOptions}
                                    onChange={value => handleContext(value)}
                                    label={'Context Level'}
                                    placeholder={t('common.select')}
                                />

                                <SelectHierarchyLabel
                                    searchable
                                    disabled={form.level === null}
                                    options={valuesOptions}
                                    value={currentContextValue}
                                    externalOptions={valuesOptions}
                                    onChange={value => handleRole('context', value)}
                                    label={'Context Value'}
                                    placeholder={t('common.select')}
                                    placeholderSearch={t('common.search')}
                                    searchValue={form.level === 2 ? queryParams.search : searchContextValue}
                                    onExternalSearch={form.level === 2 ? value => handleSearch(value) : value => setSearchContextValue(value)}
                                />
                            </>
                        )}
                    </>
                )}

                <SelectHierarchyLabel
                    searchable
                    disabled={!removeMode && !currentContextValue && contextMode === 'specific_context'}
                    value={form.role}
                    options={rolesOptions(form)}
                    externalOptions={rolesOptions(form)}
                    onChange={value => handleRole('role', value)}
                    label={t('common.role')}
                    placeholder={t('common.select')}
                    placeholderSearch={t('common.search')}
                    searchValue={searchRole}
                    onExternalSearch={value => setSearchRole(value)}
                />
            </>
        </Modal>
    );
};

export default RoleCreationModal;
